import React, { useState } from "react";
import type { Theme, StatusKey } from "../lib/theme";
import { STATUS_PALETTE } from "../lib/theme";
import type { Division, ItemState, Proposal } from "../types";
import { walkItems, inBaseBid, getItemTotal } from "../lib/calculations";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface AnalysisTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  divisions: Division[];
  itemState: Record<string, ItemState>;
  proposals: Proposal[];
  grandTotal: { byProp: Record<string, number>; subtotal: number };
  /** The computed totals, so this page can reconcile to the project total. */
  final: { subtotal: number; overhead: number; profit: number; contingency: number;
    escalation?: number; contingencyLine?: number; contingencyReserve?: number; total: number };
  projectName: string;
  fmt: (n: number) => string;
  getPC: (pid: string, props: Proposal[]) => { bg: string; text: string; bar: string };
  collapsedAnalysisDivs: Set<string>;
  setCollapsedAnalysisDivs: React.Dispatch<React.SetStateAction<Set<string>>>;
  collapsedAnalysisSubs: Set<string>;
  setCollapsedAnalysisSubs: React.Dispatch<React.SetStateAction<Set<string>>>;
  analysisStatusFilter: string | null;
  setAnalysisStatusFilter: React.Dispatch<React.SetStateAction<string | null>>;
  navigateToItem: (id: string) => void;
}

export default function AnalysisTab({
  T, isMobile, isTablet, divisions, itemState, proposals, grandTotal, final, projectName,
  fmt, getPC, collapsedAnalysisDivs, setCollapsedAnalysisDivs,
  collapsedAnalysisSubs, setCollapsedAnalysisSubs,
  analysisStatusFilter, setAnalysisStatusFilter, navigateToItem,
}: AnalysisTabProps) {
  const SK = ["Hard Bid", "Allowance", "Needs Review", "Other"];
  // Derived from the single status palette in theme.ts rather than restated —
  // this file previously declared the same four colors twice.
  const SC: Record<string, { fill: string; fillDim: string; text: string; label: string }> =
    Object.fromEntries(SK.map(k => {
      const s = STATUS_PALETTE[k as StatusKey];
      return [k, { fill: s.hue, fillDim: `${s.hue}22`, text: s.text, label: s.label }];
    }));
  // The base bid, and only the base bid -- the same money the project subtotal
  // is, by the same rule. The chart used to count everything it could see:
  // alternates nobody had accepted, and work marked By Others or Excluded,
  // which is named on a sheet precisely because somebody else is paying for
  // it. Four hundred and eighteen thousand dollars of it, under the word
  // TOTAL, and the confidence score was figured on the same inflated pile.
  const allItems = divisions.flatMap(div =>
    div.subcategories.flatMap(sub =>
      [...walkItems(sub.rows)].map(item => {
        const li = itemState[item.id] || {};
        // Through getItemTotal, so a line priced as a percentage of other
        // lines is worth here what it is worth everywhere else.
        const total = inBaseBid(li) ? getItemTotal(li, itemState) : 0;
        const status = li.costStatus && SK.includes(li.costStatus) ? li.costStatus : "Other";
        return { total, status, proposal: li.proposal, desc: li.description || item.description, div: div.code };
      }).filter(i => i.total > 0)
    )
  );

  /** What was left out, so the sheet can account for it rather than hide it. */
  const setAside = (() => {
    let alts = 0, clar = 0;
    divisions.forEach(div => div.subcategories.forEach(sub =>
      [...walkItems(sub.rows)].forEach(item => {
        const li: any = itemState[item.id] || {};
        if (!li.included) return;
        const amt = getItemTotal(li, itemState);
        if (amt <= 0) return;
        if (li.altType && !(li.altType === "Alt" && li.altStatus === "Accepted")) alts += amt;
        else if (li.costStatus === "By Others" || li.costStatus === "Excluded") clar += amt;
      })));
    return { alts, clar };
  })();
  const buildBuckets = (items: typeof allItems) => {
    const b: Record<string, number> = {};
    SK.forEach(k => { b[k] = 0; });
    items.forEach(i => { b[i.status] = (b[i.status] || 0) + i.total; });
    return { buckets: b, grandSum: Object.values(b).reduce((a, v) => a + v, 0) };
  };
  const CW: Record<string, number> = { "Hard Bid": 1, "Allowance": .5, "Needs Review": 0, "Other": .25 };
  const confScore = (items: typeof allItems) => {
    const { buckets, grandSum } = buildBuckets(items);
    if (grandSum === 0) return null;
    return Math.round((SK.reduce((a, k) => a + (buckets[k] || 0) * CW[k], 0) / grandSum) * 100);
  };

  const PieChart = ({ items, size = 200, label, sublabel, onSegmentClick }: {
    items: typeof allItems; size?: number; label: string; sublabel?: string;
    onSegmentClick?: (key: string) => void;
  }) => {
    const { buckets, grandSum } = buildBuckets(items);
    const [hov, setHov] = useState<string | null>(null);
    if (grandSum === 0) return <div style={{ textAlign: "center", padding: 20, color: T.textWeak, fontSize: 11 }}>No data</div>;
    const cx = size / 2, cy = size / 2, r = size / 2 - 6, ri = r * .52;
    let ang = -Math.PI / 2;
    const segs: { key: string; val: number; fill: string; text: string; pct: number; mid: number; d: string }[] = [];
    SK.forEach(key => {
      const val = buckets[key] || 0;
      if (!val) return;
      const sw = (val / grandSum) * 2 * Math.PI, ea = ang + sw;
      const x1 = cx + r * Math.cos(ang), y1 = cy + r * Math.sin(ang), x2 = cx + r * Math.cos(ea), y2 = cy + r * Math.sin(ea);
      const ix1 = cx + ri * Math.cos(ang), iy1 = cy + ri * Math.sin(ang), ix2 = cx + ri * Math.cos(ea), iy2 = cy + ri * Math.sin(ea);
      const lg = sw > Math.PI ? 1 : 0, mid = ang + sw / 2;
      segs.push({ key, val, fill: SC[key].fill, text: SC[key].text, pct: val / grandSum * 100, mid,
        d: `M${ix1},${iy1} L${x1},${y1} A${r},${r} 0 ${lg},1 ${x2},${y2} L${ix2},${iy2} A${ri},${ri} 0 ${lg},0 ${ix1},${iy1} Z` });
      ang = ea;
    });
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <svg width={size} height={size} style={{ overflow: "visible" }}>
          <g>
            {segs.map(s => <path key={s.key} d={s.d} fill={s.fill} opacity={hov === null || hov === s.key ? 1 : .3}
              stroke={T.bgApp} strokeWidth={1.5} style={{ cursor: onSegmentClick ? "pointer" : "default", transition: "opacity .15s" }}
              onMouseEnter={() => setHov(s.key)} onMouseLeave={() => setHov(null)} onClick={() => onSegmentClick && onSegmentClick(s.key)} />)}
            <text x={cx} y={cy - 9} textAnchor="middle" fill={T.textPrimary} fontSize={13} fontWeight={700} fontFamily={FONT}>{fmt(grandSum)}</text>
            <text x={cx} y={cy + 9} textAnchor="middle" fill={T.textWeak} fontSize={10} fontFamily={FONT}>DIRECT COST</text>
            {segs.filter(s => s.pct >= 5).map(s => {
              const mx = cx + (ri + (r - ri) / 2) * Math.cos(s.mid), my = cy + (ri + (r - ri) / 2) * Math.sin(s.mid);
              return <text key={s.key} x={mx} y={my} textAnchor="middle" dominantBaseline="middle"
                fill={T.textOnGold} fontSize={10} fontWeight={700} fontFamily={FONT} style={{ pointerEvents: "none" }}>{Math.round(s.pct)}%</text>;
            })}
          </g>
        </svg>
        <div style={{ textAlign: "center", marginTop: -10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary, marginBottom: 2 }}>{label}</div>
          {sublabel && <div style={{ fontSize: 10, color: T.textWeak }}>{sublabel}</div>}
        </div>
        <div style={{ width: "100%", maxWidth: 220 }}>
          {segs.map(s => <div key={s.key} onMouseEnter={() => setHov(s.key)} onMouseLeave={() => setHov(null)}
            onClick={() => onSegmentClick && onSegmentClick(s.key)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 3,
              cursor: onSegmentClick ? "pointer" : "default", background: hov === s.key ? T.bgHover : "transparent",
              marginBottom: 2, transition: "background .1s" }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.fill, flexShrink: 0, display: "inline-block" }} />
            <span style={{ flex: 1, fontSize: 11, color: T.textMuted }}>{SC[s.key].label}</span>
            <span style={{ fontSize: 11, color: s.text, fontWeight: 600 }}>{fmt(s.val)}</span>
            <span style={{ fontSize: 10, color: T.textMuted, minWidth: 34, textAlign: "right" }}>{s.pct.toFixed(0)}%</span>
          </div>)}
        </div>
      </div>
    );
  };

  const os = confScore(allItems);
  const sc = os === null ? T.textMuted : os >= 75 ? T.positive : os >= 45 ? T.costStatus["Needs Review"].badgeText : T.negative;

  // Line item cost status section
  const STATUS_META: Record<string, { color: string; bar: string; text: string; label: string }> =
    Object.fromEntries(SK.map(k => {
      const s = STATUS_PALETTE[k as StatusKey];
      return [k, { color: s.hue, bar: s.hue, text: s.text, label: s.label }];
    }));
  type AI = { id: string; desc: string; status: string; amt: number; divId: string; subId: string; groupName?: string };
  const lineItems: AI[] = [];
  divisions.forEach(div => div.subcategories.forEach(sub => {
    // Walk rows preserving group context so we can show the roll-up group name
    const walkWithGroup = (rows: any[], groupName?: string) => {
      rows.forEach(row => {
        if (row.type === "group") {
          walkWithGroup(row.children ?? [], row.name || undefined);
        } else {
          const li = itemState[row.id] || {};
          if (!inBaseBid(li)) return;
          const amt = getItemTotal(li, itemState);
          if (amt === 0) return;
          const s = li.costStatus && STATUS_META[li.costStatus] ? li.costStatus : "Other";
          lineItems.push({ id: row.id, desc: li.description || row.description || "", status: s, amt, divId: div.id, subId: sub.id, groupName });
        }
      });
    };
    walkWithGroup(sub.rows);
  }));
  const grandAmt = lineItems.reduce((a, i) => a + i.amt, 0);
  const statusGroups = Object.keys(STATUS_META).map(status => {
    const si = lineItems.filter(i => i.status === status);
    if (!si.length) return null;
    const byDiv = divisions.map(div => {
      const di = si.filter(i => i.divId === div.id);
      if (!di.length) return null;
      const bySub = div.subcategories.map(sub => {
        const subItems = di.filter(i => i.subId === sub.id);
        if (!subItems.length) return null;
        return { sub, items: subItems, subTotal: subItems.reduce((a, i) => a + i.amt, 0) };
      }).filter(Boolean) as { sub: any; items: AI[]; subTotal: number }[];
      return { div, bySub, divTotal: bySub.reduce((a, s) => a + s.subTotal, 0) };
    }).filter(Boolean) as { div: any; bySub: any[]; divTotal: number }[];
    return { status, meta: STATUS_META[status], byDiv, total: si.reduce((a, i) => a + i.amt, 0), count: si.length };
  }).filter(Boolean) as { status: string; meta: any; byDiv: any[]; total: number; count: number }[];

  const cardKey = (s: string) => `card__${s}`;
  const divKey = (s: string, d: string) => `div__${s}__${d}`;
  const toggleCard = (s: string) => setCollapsedAnalysisDivs(prev => { const n = new Set(prev); n.has(cardKey(s)) ? n.delete(cardKey(s)) : n.add(cardKey(s)); return n; });
  const toggleDiv = (k: string) => setCollapsedAnalysisSubs(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const [detailMode, setDetailMode] = useState<"summary" | "detail">("summary");
  const [selectedPropId, setSelectedPropId] = useState<string | null>(null);

  const viewItems = selectedPropId ? allItems.filter(i => i.proposal === selectedPropId) : allItems;
  const viewScore = confScore(viewItems);
  const vsc = viewScore === null ? T.textMuted : viewScore >= 75 ? T.positive : viewScore >= 45 ? T.costStatus["Needs Review"].badgeText : T.negative;
  const viewLabel = selectedPropId ? (proposals.find(p => p.id === selectedPropId)?.name ?? "") : projectName;
  const viewSublabel = selectedPropId ? fmt(buildBuckets(viewItems).grandSum) : `${fmt(grandTotal.subtotal)} direct cost`;
  const viewOptions = [{ id: null as string | null, label: "All Combined" }, ...proposals.map(p => ({ id: p.id, label: p.name }))];
  /** Side by side only when there is both room for it and a list to put there. */
  const twoCol = !isMobile && !isTablet && lineItems.length > 0;

  return (
    <div className="fade-in" style={{ padding: isMobile ? "14px 16px" : isTablet ? "20px 24px" : "28px 36px" }}>
      {/* Title and the package switch on one line.
          They were on two, with the confidence score pinned to the far right
          of the first — so the top of the page was a title at one edge, a
          number at the other, and a canyon of empty between them, with the
          package pills taking a whole row of their own underneath. The score
          has gone to the head of the left column where the reading starts,
          which is also where it belongs: it is the claim this screen exists
          to make. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end",
        marginBottom: 20, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.borderAccent, marginBottom: 4 }}>Cost Confidence Analysis</div>
          <div style={{ fontSize: 11, color: T.textWeak }}>Breakdown of estimate certainty by cost status</div>
        </div>
        {proposals.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as any }}>
            <span style={{ fontSize: 10, color: T.textWeak, fontFamily: FONT, letterSpacing: ".08em", textTransform: "uppercase", flexShrink: 0 }}>View:</span>
            {viewOptions.map(opt => {
              const active = selectedPropId === opt.id;
              const pc = opt.id ? getPC(opt.id, proposals) : null;
              const accentColor = pc ? pc.text : T.borderAccent;
              return (
                <button key={String(opt.id)} onClick={() => setSelectedPropId(opt.id)}
                  style={{ padding: "5px 14px", borderRadius: 999, border: `1px solid ${active ? accentColor : T.borderSecondary}`,
                    background: active ? `${accentColor}18` : "transparent", color: active ? accentColor : T.textTertiary,
                    fontFamily: FONT, fontSize: 11, fontWeight: active ? 700 : 400, minHeight: 24, cursor: "pointer", transition: "all .15s" }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* The chart beside the list rather than above it.
          A full-width list on a wide screen puts a line item's name at one
          edge and its number at the other, with a foot of empty paper
          between: the eye loses the row on the way across. Two thirds is
          still generous and the line holds together. The chart goes left
          where the reading starts, and stays put while the list scrolls --
          the share each status holds is the thing you keep checking a row
          against. */}
      <div style={{ display: "grid", alignItems: "start", gap: 28, maxWidth: 1500,
        gridTemplateColumns: twoCol ? "minmax(260px, 1fr) minmax(0, 2fr)" : "minmax(0, 1fr)" }}>
        <div style={{ position: twoCol ? "sticky" as any : undefined, top: 16,
          justifySelf: twoCol ? "stretch" : "center", maxWidth: 380, width: "100%" }}>
          {viewScore !== null && (
            <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6,
              padding: "16px 22px", marginBottom: 12, display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: vsc, lineHeight: 1, fontVariantNumeric: "tabular-nums" as any }}>{viewScore}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".12em", textTransform: "uppercase" }}>Confidence Score</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{viewScore >= 75 ? "High" : viewScore >= 45 ? "Moderate" : "Low"} confidence</div>
                <div style={{ height: 4, background: T.bgToast, borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
                  <div style={{ width: `${viewScore}%`, height: "100%", background: vsc, borderRadius: 3 }} />
                </div>
              </div>
            </div>
          )}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderAccent}33`, borderRadius: 6, padding: "28px 36px", width: "100%", boxSizing: "border-box" as any }}>
            <PieChart items={viewItems} size={200} label={viewLabel} sublabel={viewSublabel}
              onSegmentClick={key => {
                const el = document.getElementById(`cs-section-${key.replace(/\s+/g, "-").toLowerCase()}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }} />

            {/* What the total is not.
                Both of these are real money on a real drawing and neither is
                in this estimate: an alternate is priced and not bought, and a
                clarification is named so that everyone can see it is somebody
                else's to pay for. Counting them inside the total made the
                estimate look bigger than it is and the confidence score read
                off a pile that was part somebody else's. Saying so under the
                chart is the honest version of leaving them out. */}
            {/* What the direct cost becomes.
                This page totalled line items and labelled the result "total",
                while the header two inches above showed a figure nearly two
                million larger. Both were right; nothing on the page said why
                they differed, so the reader is left to assume one of them is
                wrong -- and the one they stop trusting is whichever they
                checked second. Markup is the bridge, so the bridge is drawn. */}
            {!selectedPropId && final && final.total > grandTotal.subtotal && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.borderPrimary}`, width: "100%" }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 6 }}>
                  From direct cost to the project total
                </div>
                {([
                  ["Direct cost", grandTotal.subtotal],
                  ...((final.escalation ?? 0) !== 0 ? [["Escalation", final.escalation] as const] : []),
                  ...(final.overhead > 0 ? [["Overhead", final.overhead] as const] : []),
                  ...(final.profit > 0 ? [["Profit", final.profit] as const] : []),
                  ...(final.contingency !== 0 ? [["Contingency", final.contingency] as const] : []),
                ] as Array<readonly [string, number]>).map(([label, amt]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3 }}>
                    <span style={{ flex: 1, color: T.textMuted }}>{label}</span>
                    <span style={{ color: T.textTertiary, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(amt)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginTop: 6, paddingTop: 6,
                  borderTop: `1px solid ${T.borderPrimary}` }}>
                  <span style={{ flex: 1, color: T.textSecondary, fontWeight: 700 }}>Project total</span>
                  <span style={{ color: T.textPrimary, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(final.total)}</span>
                </div>
              </div>
            )}

            {(setAside.alts > 0 || setAside.clar > 0) && !selectedPropId && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.borderPrimary}`, width: "100%" }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, marginBottom: 6 }}>
                  Not in this total
                </div>
                {setAside.alts > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3 }}>
                    <span style={{ flex: 1, color: T.textMuted }}>Alternates not accepted</span>
                    <span style={{ color: T.textTertiary, fontWeight: 600 }}>{fmt(setAside.alts)}</span>
                  </div>
                )}
                {setAside.clar > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                    <span style={{ flex: 1, color: T.textMuted }}>By others &amp; excluded</span>
                    <span style={{ color: T.textTertiary, fontWeight: 600 }}>{fmt(setAside.clar)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Line Item Cost Status cards */}
      {lineItems.length > 0 && (
        <div id="analysis-cost-status" style={{
          marginTop: twoCol ? 0 : 40, paddingTop: twoCol ? 0 : 32,
          borderTop: twoCol ? undefined : `1px solid ${T.borderPrimary}`, scrollMarginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" as any }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.borderAccent }}>Line Item Cost Status</div>
            {/* Summary / Detail toggle */}
            <div style={{ display: "flex", border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: 2, gap: 2, marginLeft: "auto" }}>
              {(["summary", "detail"] as const).map(mode => (
                <button key={mode} onClick={() => setDetailMode(mode)}
                  style={{ padding: "3px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer", fontFamily: FONT,
                    fontSize: 10, fontWeight: detailMode === mode ? 700 : 400,
                    border: detailMode === mode ? `1px solid ${T.borderAccent}40` : "1px solid transparent",
                    background: detailMode === mode ? `${T.borderAccent}15` : "transparent",
                    color: detailMode === mode ? T.borderAccent : T.textWeak,
                    transition: "all .15s" }}>
                  {mode === "summary" ? "Summary" : "Detail"}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as any }}>
              {Object.entries(STATUS_META).map(([k, v]) => {
                const isActive = analysisStatusFilter === k;
                return (
                  <button key={k} onClick={() => setAnalysisStatusFilter(isActive ? null : k)}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 9px", minHeight: 24, borderRadius: 10, cursor: "pointer", fontFamily: FONT,
                      border: `1px solid ${isActive ? v.color : v.color + "55"}`,
                      background: isActive ? `${v.color}22` : "transparent",
                      color: isActive ? v.text : T.textWeak,
                      fontWeight: isActive ? 700 : 400, fontSize: 10, transition: "all .15s" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: v.color, display: "inline-block", flexShrink: 0, opacity: isActive ? 1 : .55 }} />
                    {v.label}
                  </button>
                );
              })}
              {analysisStatusFilter && (
                <button onClick={() => setAnalysisStatusFilter(null)}
                  style={{ fontSize: 10, color: T.textWeak, background: "none", border: `1px solid ${T.borderSecondary}`, borderRadius: 10, padding: "2px 8px", minHeight: 24, cursor: "pointer", fontFamily: FONT }}>
                  Clear
                </button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 20 }}>
            {analysisStatusFilter
              ? `Showing ${STATUS_META[analysisStatusFilter]?.label} items only · ${detailMode === "summary" ? "subcategory totals" : "full line items"}.`
              : `All base-bid line items by cost status${detailMode === "summary" ? " — subcategory totals" : " — full line items"}. Click a pill to filter. Alternates excluded.`}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {statusGroups
              .filter(sg => !analysisStatusFilter || sg.status === analysisStatusFilter)
              .map(({ status, meta, byDiv, total, count }) => {
                const collapsed = collapsedAnalysisDivs.has(cardKey(status));
                const pct = grandAmt > 0 ? total / grandAmt * 100 : 0;
                return (
                  <div key={status} id={`cs-section-${status.replace(/\s+/g, "-").toLowerCase()}`}
                    style={{ background: T.bgPanel, borderRadius: 6, overflow: "hidden", scrollMarginTop: 16,
                      border: `1px solid ${meta.color}28`, borderTopWidth: 3, borderTopColor: meta.color, borderTopStyle: "solid" }}>
                    <div onClick={() => toggleCard(status)}
                      style={{ display: "flex", alignItems: "center", padding: "14px 18px", cursor: "pointer", userSelect: "none" as any }}
                      onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                      onMouseLeave={e => (e.currentTarget.style.background = "")}>
                      <span style={{ fontSize: 10, color: T.textWeak, marginRight: 8, transition: "transform .15s",
                        transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▼</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: meta.text, fontWeight: 600 }}>{meta.label}</div>
                        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{count} item{count !== 1 ? "s" : ""} · {pct.toFixed(0)}% of estimate</div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: meta.text }}>{fmt(total)}</div>
                    </div>
                    <div style={{ height: 2, background: T.bgToast }}>
                      <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: meta.bar, borderRadius: 3 }} />
                    </div>
                    {!collapsed && (
                      <div style={{ padding: "8px 18px 16px" }}>
                        {byDiv.map(({ div, bySub, divTotal }) => {
                          const dk = divKey(status, div.id);
                          const divCollapsed = collapsedAnalysisSubs.has(dk);
                          return (
                            <div key={div.id} style={{ marginBottom: 8 }}>
                              <div onClick={() => toggleDiv(dk)}
                                style={{ display: "flex", alignItems: "center", padding: "7px 10px",
                                  borderLeft: `3px solid ${meta.color}`,
                                  marginBottom: 0, cursor: "pointer", userSelect: "none" as any,
                                  borderRadius: "0 3px 3px 0" }}
                                onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                                <span style={{ fontSize: 10, color: T.textWeak, marginRight: 8, transition: "transform .15s",
                                  transform: divCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▼</span>
                                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: T.textPrimary }}>{div.code} · {div.name}</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: meta.text, minWidth: 80, textAlign: "right" }}>{fmt(divTotal)}</span>
                              </div>
                              {!divCollapsed && bySub.map(({ sub, items: subItems, subTotal }: { sub: any; items: AI[]; subTotal: number }) => (
                                <div key={sub.id} style={{ paddingLeft: 20 }}>
                                  <div style={{ display: "flex", alignItems: "center", padding: "6px 10px",
                                    borderBottom: `1px solid ${T.borderPrimary}30` }}>
                                    <span style={{ flex: 1, fontSize: 11, color: T.textSecondary, fontWeight: 400 }}>
                                      {sub.costCode && <span style={{ color: T.textTertiary, marginRight: 10, fontSize: 10, fontFamily: "monospace" }}>{sub.costCode}</span>}{sub.name}
                                    </span>
                                    <span style={{ fontSize: 11, fontWeight: 500, color: T.textPrimary, minWidth: 80, textAlign: "right" }}>{fmt(subTotal)}</span>
                                  </div>
                                  {detailMode === "detail" && (() => {
                                    // Group consecutive items by groupName so roll-up labels render as separators
                                    const rendered: React.ReactNode[] = [];
                                    let lastGroup: string | undefined = undefined;
                                    subItems.forEach((item: AI) => {
                                      if (item.groupName !== lastGroup) {
                                        lastGroup = item.groupName;
                                        if (item.groupName) {
                                          rendered.push(
                                            <div key={`grp-${item.groupName}-${item.id}`}
                                              style={{ display: "flex", alignItems: "center", padding: "5px 8px 3px 16px", marginTop: rendered.length > 0 ? 4 : 0 }}>
                                              <span style={{ fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any, flex: 1 }}>
                                                ↳ {item.groupName}
                                              </span>
                                            </div>
                                          );
                                        }
                                      }
                                      rendered.push(
                                        <div key={item.id} onClick={() => navigateToItem(item.id)}
                                          style={{ display: "flex", alignItems: "center",
                                            padding: item.groupName ? "3px 8px 3px 28px" : "4px 8px 4px 16px",
                                            borderBottom: `1px solid ${T.borderPrimary}20`, cursor: "pointer" }}
                                          onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                                          onMouseLeave={e => (e.currentTarget.style.background = "")}>
                                          <span style={{ flex: 1, fontSize: 11, color: T.textSecondary, marginRight: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as any }}>{item.desc || "—"}</span>
                                          <span style={{ fontSize: 11, color: meta.text, fontWeight: 500, flexShrink: 0, minWidth: 72, textAlign: "right" }}>{fmt(item.amt)}</span>
                                        </div>
                                      );
                                    });
                                    return rendered;
                                  })()}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
